import { useState, type FormEvent } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { apiFetch } from '../lib/api';

export function InviteUser() {
  const { user } = useAuth();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Only admins can invite. Non-admins are bounced back to the dashboard
  // (the Worker also enforces this on /api/invite).
  if (user?.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setMessage(null);
    setError(null);
    setSubmitting(true);

    try {
      await apiFetch('/api/invite', {
        method: 'POST',
        body: JSON.stringify({ email: email.trim() }),
      });
      setMessage(
        `Invitation sent to ${email}. They'll get an email with a magic link — clicking it signs them straight in. No password required.`,
      );
      setEmail('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invite failed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="page">
      <h1>Invite a user</h1>
      <p className="muted">
        Sends a Supabase invitation email containing a magic link. The new user
        starts with the <code>member</code> role in the <code>app_users</code> table;
        promote them to <code>admin</code> there if needed.
      </p>
      <form onSubmit={onSubmit} className="auth-card" noValidate>
        <label>
          Email
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>
        {error && <p className="form-error" role="alert">{error}</p>}
        {message && <p className="form-success" role="status">{message}</p>}
        <button type="submit" disabled={submitting}>
          {submitting ? 'Sending…' : 'Send invite'}
        </button>
      </form>
    </section>
  );
}
